// Drag-to-reorder + show/hide for sidebar items. HTML5 drag-and-drop (no extra deps).
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";

const CustomizeSidebarDialog = ({ open, onOpenChange, allItems, layout, onSave }) => {
  const [draft, setDraft] = useState({ order: [], hidden: [] });
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Build the current display order so the dialog matches what the user sees
    const byPath = Object.fromEntries(allItems.map((i) => [i.path, i]));
    const out = [];
    const seen = new Set();
    for (const p of (layout.order || [])) {
      if (byPath[p]) { out.push(p); seen.add(p); }
    }
    for (const item of allItems) {
      if (!seen.has(item.path)) out.push(item.path);
    }
    setDraft({ order: out, hidden: [...(layout.hidden || [])] });
  }, [open, layout, allItems]);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) return;
    const next = [...draft.order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setDraft({ ...draft, order: next });
    setDragIdx(null);
  };

  const toggleHide = (path) => {
    const hidden = draft.hidden.includes(path)
      ? draft.hidden.filter((p) => p !== path)
      : [...draft.hidden, path];
    setDraft({ ...draft, hidden });
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...draft.order];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setDraft({ ...draft, order: next });
  };
  const moveDown = (idx) => {
    if (idx === draft.order.length - 1) return;
    const next = [...draft.order];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setDraft({ ...draft, order: next });
  };

  const handleReset = () => setDraft({ order: allItems.map((i) => i.path), hidden: [] });

  const handleSave = async () => {
    await onSave(draft);
    onOpenChange(false);
  };

  const byPath = Object.fromEntries(allItems.map((i) => [i.path, i]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-testid="customize-sidebar-dialog">
        <DialogHeader>
          <DialogTitle>Customise menu</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          Drag the handles to reorder. Click the eye to hide items you don't need.
        </p>
        <div className="flex-1 overflow-y-auto space-y-1 mt-2 pr-1">
          {draft.order.map((path, idx) => {
            const item = byPath[path];
            if (!item) return null;
            const Icon = item.icon;
            const isHidden = draft.hidden.includes(path);
            return (
              <div
                key={path}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                className={`group flex items-center gap-2 px-2 py-2 border border-zinc-200 dark:border-zinc-700 rounded cursor-move bg-white dark:bg-zinc-900 ${dragIdx === idx ? "opacity-40" : ""} ${isHidden ? "opacity-60" : ""}`}
                data-testid={`customize-row-${path.slice(1)}`}
              >
                <GripVertical className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <Icon className="w-4 h-4 text-zinc-600 dark:text-zinc-400 flex-shrink-0" />
                <span className={`flex-1 text-sm ${isHidden ? "line-through text-zinc-400" : "text-zinc-900 dark:text-zinc-100"}`}>
                  {item.label}
                </span>
                <div className="flex items-center gap-0.5 opacity-70 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="px-1.5 py-1 text-[10px] text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:opacity-30"
                    title="Move up"
                    data-testid={`customize-up-${path.slice(1)}`}
                  >▲</button>
                  <button
                    type="button"
                    onClick={() => moveDown(idx)}
                    disabled={idx === draft.order.length - 1}
                    className="px-1.5 py-1 text-[10px] text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:opacity-30"
                    title="Move down"
                    data-testid={`customize-down-${path.slice(1)}`}
                  >▼</button>
                  <button
                    type="button"
                    onClick={() => toggleHide(path)}
                    className={`p-1 ${isHidden ? "text-amber-600" : "text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"}`}
                    title={isHidden ? "Show in sidebar" : "Hide from sidebar"}
                    data-testid={`customize-toggle-${path.slice(1)}`}
                  >
                    {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-200 dark:border-zinc-800">
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 inline-flex items-center gap-1"
            data-testid="customize-reset-btn"
          >
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded"
              data-testid="customize-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded"
              data-testid="customize-save-btn"
            >
              Save
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomizeSidebarDialog;
